import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  useTheme,
} from '@mui/material';
import { FeedOutlined, Refresh } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '@/store/types';
import { fetchFeeds, loadMoreFeeds } from '@/store';
import {
  FeedItem,
  FeedSkeleton,
  MediaPreviewModals
} from './feed';
import { Moment } from 'moment';

interface FeedListProps {
  refreshTrigger?: number;
  selectedType?: string;
  searchQuery?: string;
  startDate?: Moment | null;
  endDate?: Moment | null;
  userId?: string;
  hasAttachment?: boolean;
  // Job-specific filters
  location?: string;
  experienceLevel?: string;
  jobType?: string;
  minSalary?: string;
  maxSalary?: string;
  salaryDenomination?: string;
  salaryPeriod?: string;
}

export const FeedList: React.FC<FeedListProps> = ({
  refreshTrigger,
  selectedType,
  searchQuery,
  startDate,
  endDate,
  userId,
  hasAttachment,
  location,
  experienceLevel,
  jobType,
  minSalary,
  maxSalary,
  salaryDenomination,
  salaryPeriod,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();

  const { feeds, pagination, isFeedsLoading, error } = useSelector((state: RootState) => state.feed);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = React.useState(false);
  const [imagePreview, setImagePreview] = useState<{ open: boolean; url: string; alt: string }>({ 
    open: false, url: '', alt: '' 
  });
  const [documentPreview, setDocumentPreview] = useState<{ open: boolean; url: string; fileName: string }>({ 
    open: false, url: '', fileName: '' 
  });
  const [expandedContent, setExpandedContent] = useState<Set<string>>(new Set());
  const [openCommentsFeedId, setOpenCommentsFeedId] = useState<string | null>(null);


  useEffect(() => {
    const filters: any = {
      page: 1,
      limit: 7,
      sort: '-updatedAt',
      visibility: 'public',
    };

    if (userId) {
      filters.userId = userId;
    }

    if (selectedType && selectedType !== 'all') {
      filters.feedType = selectedType;
    }

    if (searchQuery && searchQuery.trim()) {
      filters.search = searchQuery.trim();
    }

    if (startDate) {
      filters.startDate = startDate.format('YYYY-MM-DD');
    }

    if (endDate) {
      filters.endDate = endDate.format('YYYY-MM-DD');
    }

    if (hasAttachment) {
      filters.hasAttachment = true;
    }

    // Job-specific filters
    if (location && location.trim()) {
      filters.location = location.trim();
    }

    if (experienceLevel) {
      filters.experienceLevel = experienceLevel;
    }

    if (jobType) {
      filters.jobType = jobType;
    }

    if (minSalary) {
      filters.minSalary = minSalary;
    }

    if (maxSalary) {
      filters.maxSalary = maxSalary;
    }

    if (salaryDenomination && (minSalary || maxSalary)) {
      filters.salaryDenomination = salaryDenomination;
    }

    if (salaryPeriod && (minSalary || maxSalary)) {
      filters.salaryPeriod = salaryPeriod;
    }

    dispatch(fetchFeeds(filters));
  }, [dispatch, refreshTrigger, selectedType, searchQuery, startDate, endDate, userId, hasAttachment, location, experienceLevel, jobType, minSalary, maxSalary, salaryDenomination, salaryPeriod]);

  // Track when we've completed the initial load
  useEffect(() => {
    if (!isFeedsLoading && !hasInitiallyLoaded) {
      setHasInitiallyLoaded(true);
    }
  }, [isFeedsLoading, hasInitiallyLoaded]);


  const handleImageClick = (url: string, alt: string) => {
    setImagePreview({ open: true, url, alt });
  };

  const handleCloseImagePreview = () => {
    setImagePreview({ open: false, url: '', alt: '' });
  };

  const handleDocumentClick = (url: string, fileName: string) => {
    const encodedUrl = encodeURIComponent(url);
    const viewerUrl = `https://docs.google.com/viewer?url=${encodedUrl}&embedded=true`;
    setDocumentPreview({ open: true, url: viewerUrl, fileName });
  };

  const handleCloseDocumentPreview = () => {
    setDocumentPreview({ open: false, url: '', fileName: '' });
  };

  const handleLoadMoreFeeds = () => {
    if (pagination && pagination.current < pagination.pages && !isFeedsLoading) {
      const filters: any = {
        page: pagination.current + 1,
        limit: 7,
        sort: '-createdAt',
        visibility: 'public',
      };

      if (userId) {
        filters.userId = userId;
      }

      if (selectedType && selectedType !== 'all') {
        filters.feedTypeId = selectedType;
      }

      if (searchQuery && searchQuery.trim()) {
        filters.search = searchQuery.trim();
      }

      if (startDate) {
        filters.startDate = startDate.format('YYYY-MM-DD');
      }

      if (endDate) {
        filters.endDate = endDate.format('YYYY-MM-DD');
      }

      if (hasAttachment) {
        filters.hasAttachment = true;
      }

      // Job-specific filters
      if (location && location.trim()) {
        filters.location = location.trim();
      }

      if (experienceLevel) {
        filters.experienceLevel = experienceLevel;
      }

      if (jobType) {
        filters.jobType = jobType;
      }

      if (minSalary) {
        filters.minSalary = minSalary;
      }

      if (maxSalary) {
        filters.maxSalary = maxSalary;
      }

      if (salaryDenomination && (minSalary || maxSalary)) {
        filters.salaryDenomination = salaryDenomination;
      }

      if (salaryPeriod && (minSalary || maxSalary)) {
        filters.salaryPeriod = salaryPeriod;
      }

      dispatch(loadMoreFeeds(filters));
    }
  };

  const renderSkeletonItems = () => (
    <>
      {Array.from({ length: 3 }).map((_, index) => (
        <FeedSkeleton key={index} />
      ))}
    </>
  );

  const renderEmptyState = () => (
    <Paper
      elevation={0}
      sx={{
        p: 6,
        textAlign: 'center',
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'grey.900',
      }}
    >
      <FeedOutlined 
        sx={{ 
          fontSize: 64, 
          color: 'text.disabled',
          mb: 2 
        }} 
      />
      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
        No feeds available at the moment
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
        It looks like there are no posts to show right now. Be the first to share something with the community!
      </Typography>
      <Button
        variant="outlined"
        startIcon={<Refresh />}
        onClick={() => {
          const filters: any = {
            page: 1,
            limit: 10,
            sort: '-updatedAt',
            visibility: 'public',
          };

          if (userId) {
            filters.userId = userId;
          }

          if (selectedType && selectedType !== 'all') {
            filters.feedTypeId = selectedType;
          }

          if (searchQuery && searchQuery.trim()) {
            filters.search = searchQuery.trim();
          }

          if (startDate) {
            filters.startDate = startDate.format('YYYY-MM-DD');
          }

          if (endDate) {
            filters.endDate = endDate.format('YYYY-MM-DD');
          }

          if (hasAttachment) {
            filters.hasAttachment = true;
          }

          // Job-specific filters
          if (location && location.trim()) {
            filters.location = location.trim();
          }

          if (experienceLevel) {
            filters.experienceLevel = experienceLevel;
          }

          if (jobType) {
            filters.jobType = jobType;
          }

          if (minSalary) {
            filters.minSalary = minSalary;
          }

          if (maxSalary) {
            filters.maxSalary = maxSalary;
          }

          if (salaryDenomination && (minSalary || maxSalary)) {
            filters.salaryDenomination = salaryDenomination;
          }

          if (salaryPeriod && (minSalary || maxSalary)) {
            filters.salaryPeriod = salaryPeriod;
          }

          dispatch(fetchFeeds(filters));
        }}
        sx={{ textTransform: 'none' }}
      >
        Refresh Feed
      </Button>
    </Paper>
  );

  const renderLoadMoreButton = () => {
    if (!pagination || pagination.current >= pagination.pages) return null;

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <Button
          variant="outlined"
          onClick={handleLoadMoreFeeds}
          disabled={isFeedsLoading}
          startIcon={isFeedsLoading ? <CircularProgress size={16} /> : null}
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            px: 4,
            py: 1.5,
            fontSize: '0.875rem',
            fontWeight: 500,
          }}
        >
          {isFeedsLoading ? 'Loading...' : 'Load More Posts'}
        </Button>
      </Box>
    );
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }
  return (
    <Box>
      {isFeedsLoading && feeds?.length === 0 ? (
        renderSkeletonItems()
      ) : feeds?.length === 0 && !isFeedsLoading ? (
        renderEmptyState()
      ) : (
        <>
          {feeds?.map(feed => (
            <FeedItem
              key={feed._id}
              feed={feed}
              expandedContent={expandedContent}
              setExpandedContent={setExpandedContent}
              onImageClick={handleImageClick}
              onDocumentClick={handleDocumentClick}
              openCommentsFeedId={openCommentsFeedId}
              setOpenCommentsFeedId={setOpenCommentsFeedId}
            />
          ))}
          
          {renderLoadMoreButton()}
          
          {isFeedsLoading && pagination && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          )}
        </>
      )}
      
      <MediaPreviewModals
        imagePreview={imagePreview}
        documentPreview={documentPreview}
        onCloseImagePreview={handleCloseImagePreview}
        onCloseDocumentPreview={handleCloseDocumentPreview}
      />
    </Box>
  );
};